import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from "@mui/material";
import React from "react";
import { ICollaborator } from "../../shared/types/collaborators.types";
import { CustomAvatar } from "../avatar/CustomAvatar";
import { useAppSelector } from "../../hooks/useAppSelector";
import { Dayjs } from "dayjs";

type Props = {
  selectedCollaborator?: string;
  handleSelectChange: (event: SelectChangeEvent<string>) => void;
  sx?: SxProps<Theme>;
  shownCollaborators?: "all" | "active" | "dateActive";
  label?: string;
  dateActive?: Dayjs;
  allowNoneOption?: boolean;
  /** When provided, only collaborators whose ID is in this set will be shown (applied after other filters) */
  allowedCollaboratorIds?: string[];
};
export const CollaboratorSelect: React.FC<Props> = ({
  selectedCollaborator,
  handleSelectChange,
  shownCollaborators = "all",
  sx,
  label = "Collaborator",
  dateActive,
  allowNoneOption = false,
  allowedCollaboratorIds,
}) => {
  // @ts-ignore
  const { collaborators } = useAppSelector((state) => state.collaborator);

  const sortedCollaborators = ([...collaborators] as ICollaborator[]).sort(
    (a, b) => a.col_code.localeCompare(b.col_code)
  );

  const filteredCollaborators =
    shownCollaborators === "active"
      ? sortedCollaborators.filter((collaborator) => collaborator.isActive)
      : shownCollaborators === "dateActive"
      ? sortedCollaborators.filter((collaborator) => {
          const dateActiveDate = dateActive ? dateActive.toDate() : null;

          const collaboratorStartDate = collaborator.startDate
            ? new Date(collaborator.startDate)
            : null;
          if (!dateActiveDate || !collaboratorStartDate) return false;

          const collaboratorEndDate = collaborator.endDate
            ? new Date(collaborator.endDate)
            : null;

          const startDateIsBefore = collaboratorStartDate <= dateActiveDate;

          const endDateIsAfter =
            !collaboratorEndDate || collaboratorEndDate >= dateActiveDate;

          return startDateIsBefore && endDateIsAfter;
        })
      : sortedCollaborators;

  const displayedCollaborators = allowedCollaboratorIds
    ? filteredCollaborators.filter((c) => allowedCollaboratorIds.includes(c.id))
    : filteredCollaborators;

  return (
    <FormControl fullWidth sx={sx}>
      <InputLabel id="collaborator-label">{label}</InputLabel>
      <Select
        labelId="collaborator-label"
        value={selectedCollaborator ?? ""}
        onChange={handleSelectChange}
        label={label}
      >
        {allowNoneOption && (
          <MenuItem value={""}>
            <Box display="flex" alignItems="center">
              <Box ml={1}>None</Box>
            </Box>
          </MenuItem>
        )}
        {displayedCollaborators.map((collaborator: ICollaborator) => {
          return (
            <MenuItem key={collaborator.id} value={collaborator.id}>
              <Box display="flex" alignItems="center">
                <CustomAvatar collaborator={collaborator} />
                <Box ml={1}>{collaborator.col_code}</Box>
              </Box>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
